import { ApiClientError } from './api';
import { AuthTokenService } from './auth-token-service';
import { authLogger } from './logger';

export interface AuthErrorContext {
  isValidating?: boolean;
  isAutoLogin?: boolean;
  pathname?: string;
  context?:
    | 'token_refresh'
    | 'token_validation'
    | 'login'
    | 'register'
    | 'api_call';
  clearAuth?: boolean;
}

export class AuthErrorHandler {
  private static redirecting = false;
  private static lastRedirectTime = 0;
  private static readonly REDIRECT_COOLDOWN = 2000; // 2 seconds

  /**
   * Unified error handler for all authentication errors
   */
  static handleAuthError(error: unknown, context: AuthErrorContext = {}) {
    authLogger.debug('Handling auth error:', { error, context });

    // Handle ApiClientError
    if (error instanceof ApiClientError) {
      if (error.status === 401) {
        this.handle401(error, context);
      } else if (error.status === 403) {
        this.handle403(error);
      }
      return;
    }

    // Handle network errors
    if (this.isNetworkError(error)) {
      if (error instanceof Error) {
        this.handleNetworkError(error);
      }
      return;
    }

    // Handle token expiration
    if (
      context.context === 'token_refresh' ||
      context.context === 'token_validation'
    ) {
      this.handleTokenExpired();
      return;
    }

    // Clear auth if requested
    if (context.clearAuth) {
      const pathname =
        context.pathname ||
        (typeof window !== 'undefined' ? window.location.pathname : '');
      if (!this.isAuthPage(pathname)) {
        void this.clearAuthAndRedirect(pathname);
      }
    }
  }

  /**
   * Handle 401 authentication errors
   */
  static handle401(error: ApiClientError, context: AuthErrorContext = {}) {
    // A late 401 for a request sent with an older token (or none) must not
    // sign out the session that replaced it, e.g. a user who just logged in.
    if (!error.sentWithCurrentToken) {
      authLogger.debug('401 for a request sent with a stale token, ignoring');
      return;
    }

    if (AuthTokenService.isSwitchingSession) {
      authLogger.debug('401 during a sign-in method add, ignoring');
      return;
    }

    // Prevent handling during login/register attempts
    if (context.context === 'login' || context.context === 'register') {
      authLogger.debug('401 during login/register, skipping handler');
      return;
    }

    // Prevent handling during token validation
    if (context.isValidating) {
      authLogger.debug('401 during token validation, skipping handler');
      return;
    }

    // Prevent handling during auto-login
    if (context.isAutoLogin) {
      authLogger.debug('401 during auto-login, skipping handler');
      return;
    }

    // Get current pathname
    const pathname =
      context.pathname ||
      (typeof window !== 'undefined' ? window.location.pathname : '');

    // Don't handle on auth pages
    if (this.isAuthPage(pathname)) {
      authLogger.debug('401 on auth page, skipping handler');
      return;
    }

    // Prevent redirect loops with cooldown
    const now = Date.now();
    if (
      this.redirecting ||
      now - this.lastRedirectTime < this.REDIRECT_COOLDOWN
    ) {
      authLogger.debug('Redirect cooldown active, skipping');
      return;
    }

    // Clear auth and redirect
    void this.clearAuthAndRedirect(pathname);
  }

  /**
   * Handle 403 forbidden errors
   */
  static handle403(error: ApiClientError) {
    authLogger.warn('403 Forbidden error:', error.message);

    // Could redirect to a permission denied page or show a notification
    // For now, we'll just log it
  }

  /**
   * Handle network errors
   */
  static handleNetworkError(error: Error) {
    authLogger.error('Network error:', error.message);

    // Don't clear auth on network errors - might be temporary
    // Could show a network error notification
  }

  /**
   * Handle token expiration
   */
  static handleTokenExpired() {
    authLogger.info('Token expired, clearing auth');

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';

    if (!this.isAuthPage(pathname)) {
      void this.clearAuthAndRedirect(pathname);
    }
  }

  /**
   * Clear authentication and redirect to login
   *
   * IMPORTANT: IndexedDB (TanStack Query persister) must be wiped before the
   * navigation fires. A `window.location.replace` triggers a fresh page load
   * that rehydrates the query cache from IDB — if we don't clear it first, the
   * previous user's projects/goals/etc. hydrate into the new session before
   * any network call. Mirrors AuthManager.handleSessionExpired on Apple.
   */
  private static async clearAuthAndRedirect(currentPath: string) {
    if (typeof window === 'undefined') return;

    this.redirecting = true;
    this.lastRedirectTime = Date.now();

    authLogger.info('Clearing auth and redirecting to login');

    // Clear all auth data
    await AuthTokenService.clearAuth();

    // Wipe persisted TanStack Query cache so the next page load can't
    // rehydrate the previous user's data.
    try {
      const { cacheInvalidation } = await import('./cache-invalidation');
      await cacheInvalidation.afterLogout();
    } catch (err) {
      authLogger.error('Failed to clear query cache on 401:', err);
    }

    // Build redirect URL
    let loginUrl = '/auth/login';
    if (currentPath && currentPath !== '/') {
      // Keep the query of the page being left (e.g. the OAuth consent screen
      // is useless without its authorize parameters).
      const returnTo =
        currentPath === window.location.pathname
          ? `${currentPath}${window.location.search}`
          : currentPath;
      loginUrl += `?redirect=${encodeURIComponent(returnTo)}`;
    }

    // Use replace to avoid history pollution
    window.location.replace(loginUrl);

    // Reset flag after a delay (in case redirect fails)
    setTimeout(() => {
      this.redirecting = false;
    }, 5000);
  }

  /**
   * Check if current path is an auth page or checkout page
   * These pages should not trigger automatic redirects on auth errors
   */
  private static isAuthPage(pathname: string): boolean {
    const authPaths = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/google/callback',
      '/auth/apple/callback',
      '/auth/vk/callback',
      '/auth/auto-login',
      '/activate',
    ];

    return authPaths.some((path) => pathname.startsWith(path));
  }

  /**
   * Format error message for display
   */
  static formatErrorMessage(error: unknown): string {
    if (error instanceof ApiClientError) {
      return error.getFirstError() || 'An error occurred';
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An unexpected error occurred';
  }

  /**
   * Check if error is authentication related
   */
  static isAuthError(error: unknown): boolean {
    if (error instanceof ApiClientError) {
      return error.status === 401 || error.status === 403;
    }

    return false;
  }

  /**
   * Check if error is network related
   */
  static isNetworkError(error: unknown): boolean {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return true;
    }

    if (error instanceof ApiClientError && !error.status) {
      return true;
    }

    return false;
  }
}
